import { useState, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { IMAGE_GATEWAY_URL } from '../config/images';

interface UploadResult {
  key: string;
  url: string;
}

interface UseImageUploadReturn {
  isUploading: boolean;
  progress: number;
  error: string | null;
  uploadImage: (file: File, vendorId: number, type: string, entityId: number | string) => Promise<string | null>;
  deleteImage: (key: string) => Promise<boolean>;
}

export function useImageUpload(): UseImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = useCallback(async (
    file: File,
    vendorId: number,
    type: string,
    entityId: number | string
  ): Promise<string | null> => {
    setIsUploading(true);
    setProgress(0);
    setError(null);

    try {
      // 1. Compress image (try WebP, iOS Safari falls back to JPEG)
      setProgress(10);
      const compressed = await imageCompression(file, {
        maxSizeMB: 0.5,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
        fileType: 'image/webp',
      });
      setProgress(40);

      // 2. Detect actual output format (iOS Safari can't produce WebP)
      const actualType = compressed.type || 'image/jpeg';
      const ext = actualType === 'image/webp' ? 'webp'
               : actualType === 'image/png' ? 'png'
               : 'jpg';

      // 3. Build key with timestamp for immutability
      const key = `v/${vendorId}/${type}/${entityId}/${Date.now()}.${ext}`;

      // 3. Upload to Worker with JWT
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }

      const result = await new Promise<UploadResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${IMAGE_GATEWAY_URL}/${key}`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('Content-Type', actualType);

        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(40 + Math.round((e.loaded / e.total) * 50));
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              const errBody = JSON.parse(xhr.responseText);
              reject(new Error(errBody.error || `Upload failed (${xhr.status})`));
            } catch {
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(compressed);
      });

      setProgress(100);
      return result.key;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setError(message);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  const deleteImage = useCallback(async (key: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;

      const res = await fetch(`${IMAGE_GATEWAY_URL}/${key}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  return { isUploading, progress, error, uploadImage, deleteImage };
}
